import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { chunk } from "lodash";
import { Box, Stack, Cluster, Checkbox, Text } from "@thecb/components";
import { TableRow } from "@/components/generic/Table";
import { TableRowEntry } from "@/components/generic/NewTable";
import {
  LineItemDescription,
  LineItemSubDescription,
  AmountError,
  CurrencyInput,
} from "./PartialLineItem.styled";
import { WHITE, BACKGROUND_GREY } from "@/constants/colors";
import { amountToCents } from "@/util/common";

const PartialLineItem = ({
  lineItem,
  description,
  subDescription,
  previousRefunds,
  isRefunded,
  setIsRefunded,
  refundAmount,
  setRefundAmount,
  goToPayment,
}) => {
  const amountRefundable = lineItem?.amountRefundableInCents?.value ?? 0;
  const refundable = amountRefundable > 0;
  const background = refundable ? WHITE : BACKGROUND_GREY;
  const defaultDescription = `Item ${lineItem.id.value}`;
  const defaultSubDescription = "Not Available";

  const handleCheck = () => {
    setIsRefunded({ lineItem, isRefunded: !isRefunded });
  };

  const [amountValue, setAmountValue] = useState(refundAmount);

  useEffect(() => {
    // Don't overwrite empty input with 0.
    if (!(refundAmount === 0 && amountValue === "")) {
      setAmountValue(refundAmount);
    }
  }, [amountValue, refundAmount]);

  const handleChange = ({ floatValue }) => {
    const amountValue = floatValue;

    setAmountValue(amountValue);

    setRefundAmount({
      lineItem,
      // Treat empty input as 0.
      amount: amountValue ?? 0,
    });
  };

  const formatPreviousRefunds = (refundData) => {
    const refundBoxes = refundData.map((refund, index) => {
      const separator =
        (index + 1) % 3 === 0 || index === refundData.length - 1 ? "" : " | ";

      return (
        <Text
          key={refund.id.value}
          extraStyles={"font-size: 12px; white-space: pre; line-height: 20px;"}
        >
          <Link
            to={`/payment/${refund.id.value}`}
            onClick={() => goToPayment({ transactionId: refund.id.value })}
          >
            {refund.id.value}
          </Link>
          {separator}
        </Text>
      );
    });

    return chunk(refundBoxes, 3).map((refundRow, index) => (
      <Cluster key={index} justify="flex-end" extraStyles={"line-height: 20px;"}>
        {refundRow}
      </Cluster>
    ));
  };

  const displayPreviousRefunds = previousRefunds.length > 0 && (
    <Box padding="0">
      <Stack direction="row" childGap=".5rem">
        <Text extraStyles={"font-size: 12px; line-height: 19px;"}>
          {"Previous refunds: "}
        </Text>
        <Stack justify="flex-start" childGap="0">
          {formatPreviousRefunds(previousRefunds)}
        </Stack>
      </Stack>
    </Box>
  );

  const getAmountError = () => {
    const safeRefundableAmount = Number(amountRefundable) || 0;
    const safeEnteredAmount = Number(amountValue) || 0;

    const maxAmountInCents = BigInt(safeRefundableAmount);
    const enteredAmountInCents = amountToCents(safeEnteredAmount);

    if (enteredAmountInCents < 0) {
      return "Invalid Amount";
    } else if (maxAmountInCents < enteredAmountInCents) {
      return "More than allowed";
    } else {
      return "";
    }
  };

  const amountErrorMessage = getAmountError();

  return (
    <TableRow background={background} noHover autoHeight>
      <TableRowEntry padding={"16px"}>
        <Cluster nowrap align="center">
          <Checkbox
            name={`Line Item ${lineItem.id.value}`}
            checked={isRefunded}
            onChange={handleCheck}
            disabled={!refundable}
          />
          <Stack>
            <LineItemDescription>
              {description || defaultDescription}
            </LineItemDescription>
            <LineItemSubDescription>
              {subDescription || defaultSubDescription}
            </LineItemSubDescription>
          </Stack>
        </Cluster>
      </TableRowEntry>
      <TableRowEntry padding={"16px"}>
        <Cluster justify="flex-end" childGap="1rem" align="center">
          <Stack justify="flex-end" align="end" childGap="1px">
            <Text weight="600" extraStyles={"font-size: 14px; text-align: right"}>
              {`$${(amountRefundable / 100).toFixed(2)} refundable`}
            </Text>
            {displayPreviousRefunds}
          </Stack>
          {isRefunded && (
            <Stack>
              <CurrencyInput
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale={true}
                value={amountValue === undefined ? 0 : amountValue}
                $isValid={amountErrorMessage === ""}
                onValueChange={handleChange}
              />
              {amountErrorMessage !== "" && (
                <AmountError>{amountErrorMessage}</AmountError>
              )}
            </Stack>
          )}
        </Cluster>
      </TableRowEntry>
    </TableRow>
  );
};

export default PartialLineItem;
